import type { InputState } from "./state";

const R = Math.PI / 180;
/** Below this many °/s on an axis it is the hand trembling, not a turn. */
export const TREMBLE = 1;
/** The longest interval one motion event may stand for, so a page waking from sleep does not spin. */
const MAX_DT = 0.1;

export interface Rate { alpha: number | null; beta: number | null; gamma: number | null }
export type PermissionRequest = () => Promise<"granted" | "denied" | "prompt">;

const still = (v: number) => (Math.abs(v) < TREMBLE ? 0 : v);

/**
 * A rotation rate (°/s about the phone's own axes, W3C devicemotion) to a camera turn in radians, for how the
 * screen is turned: yaw follows the axis that points up the screen, pitch the axis that points right along
 * it. devicemotion names the rates by axis, alpha about x and beta about y, unlike deviceorientation's angles;
 * the twist about the screen (gamma, z) is left out. One turn of the phone is one turn of the view.
 */
export function gyroTurn(rate: Rate, angle: number, dt: number): { yaw: number; pitch: number } {
  const x = rate.alpha ?? 0, y = rate.beta ?? 0;
  // [rate about screen-up, rate about screen-right]: the phone's y is up when upright, its x when turned to 90°
  const [up, right] = angle === 90 ? [x, -y] : angle === 180 ? [-y, -x] : angle === 270 ? [-x, y] : [y, x];
  // turning right is clockwise about screen-up; looking down is the top edge tipping away, clockwise about screen-right
  return { yaw: still(-up) * R * dt || 0, pitch: still(-right) * R * dt || 0 };
}

/** How far the screen is turned from upright, 0–359: screen.orientation, or iOS Safari's window.orientation. */
export function screenAngle(s: { orientation?: { angle: number } } = screen, legacy: number | undefined = (window as { orientation?: number }).orientation): number {
  const a = s.orientation?.angle ?? legacy ?? 0;
  return ((a % 360) + 360) % 360;
}

/** Motion events into turns while on() holds; the interval is measured between events. */
export function bindGyro(input: InputState, target: Window = window,
  deps: { on: () => boolean; angle?: () => number; now?: () => number }): void {
  const angle = deps.angle ?? (() => screenAngle());
  const now = deps.now ?? (() => performance.now());
  let last: number | null = null;
  target.addEventListener("devicemotion", (e) => {
    const t = now();
    const dt = last === null ? 0 : Math.min((t - last) / 1000, MAX_DT);
    last = t;
    const rate = (e as DeviceMotionEvent).rotationRate;
    if (!rate || !deps.on()) return;
    const turn = gyroTurn(rate, angle(), dt);
    if (turn.yaw || turn.pitch) input.addTurn(turn.yaw, turn.pitch);
  });
}

/** The browser's motion permission request (iOS Safari, recent Chromium), or null where there is none. */
export function motionPermission(): PermissionRequest | null {
  const D = (globalThis as { DeviceMotionEvent?: { requestPermission?: PermissionRequest } }).DeviceMotionEvent;
  return typeof D?.requestPermission === "function" ? D.requestPermission.bind(D) : null;
}

/**
 * False only when the player refused motion. iOS only answers a request made from a tap; Chromium has the
 * same call and answers "prompt" while nothing is decided, which is not a refusal.
 */
export async function askGyro(request: PermissionRequest | null = motionPermission()): Promise<boolean> {
  if (!request) return true;
  try { return (await request()) !== "denied"; } catch { return false; }
}

/** Ask from the first tap of the page while the gyro is on (iOS needs it); a refusal is reported once. */
export function bindGyroPermission(target: Window = window, request: PermissionRequest | null = motionPermission(),
  on: () => boolean, denied: () => void): void {
  if (!request) return;
  let asked = false;
  target.addEventListener("touchend", () => {
    if (asked || !on()) return;
    asked = true;
    void askGyro(request).then((ok) => { if (!ok) denied(); });
  }, true);
}
